import React, { useMemo } from 'react';
import { Card, Frame, SageClassnames } from '@kajabi/sage-react';
import copy from 'copy-to-clipboard';

import type { MessageType } from 'common/components/CopyMagic/contexts/MessagesContext/MessagesContext.types';
import CodeSnippet from 'common/components/CopyMagic/components/CodeSnippet';
import MessageFeedbackSentiment from 'common/components/CopyMagic/generators/ConversationAssistant/components/MessageFeedbackSentiment';
import CopyButton from 'common/components/CopyMagic/components/CopyButton/CopyButton';
import useGeneratorTrackingCallback from 'common/components/CopyMagic/hooks/useGeneratorTrackingCallback';
import {
  MESSAGE_FRAGMENT_TYPES,
  OPEN_AI_CODE_WRAPPER,
} from 'common/components/CopyMagic/constants';

type Props = {
  message: MessageType;
};

type FragmentType = {
  type: 'code' | 'text';
  content: string;
};

const parseMessageContent = (
  content: string,
): { fragments: FragmentType[]; copyableContent: string } => {
  const copyMagicContent = content
    .replace(/```[a-zA-Z]*\n(.*?)\n```/gs, '<copy-magic-code>$1</copy-magic-code>')
    .replace(/```(.*?)```/gs, '<copy-magic-code>$1</copy-magic-code>');
  const partialFragments = copyMagicContent.split('<copy-magic-code>');
  let copyableContent = '';
  const fragments = partialFragments.reduce((accumulator: FragmentType[], fragment: string) => {
    if (fragment.indexOf('</copy-magic-code>') !== -1) {
      const [code, text] = fragment.split('</copy-magic-code>');
      accumulator.push({ type: MESSAGE_FRAGMENT_TYPES.CODE, content: code });
      copyableContent += code;
      accumulator.push({ type: MESSAGE_FRAGMENT_TYPES.TEXT, content: text });
      copyableContent += text;
    } else {
      accumulator.push({ type: MESSAGE_FRAGMENT_TYPES.TEXT, content: fragment });
      copyableContent += fragment;
    }
    return accumulator;
  }, []);
  return { fragments, copyableContent };
};

const MessageFragment = ({
  messageFragment,
  children,
}: {
  messageFragment: FragmentType;
  children?: React.ReactNode;
}) => {
  if (messageFragment.type === MESSAGE_FRAGMENT_TYPES.CODE) {
    return (
      <>
        <CodeSnippet code={messageFragment.content} />
        {children}
      </>
    );
  }

  // When copying content within a fragment. Duplicate Copy Button logic to
  // ensure consistency and that it renders with breaks in RTE.
  const handleCopyCapture = () => {
    const value = document.getSelection()?.toString() || '';

    if (messageFragment.content.includes(value)) {
      setTimeout(() => {
        copy(value, { format: 'text/plain' });
      }, 100);
    }
  };

  return (
    <>
      <p
        onCopyCapture={() => {
          handleCopyCapture();
        }}
        className={SageClassnames.TYPE.BODY_SMALL_MED}
      >
        {messageFragment.content}
      </p>
      {children}
    </>
  );
};

const Assistant = ({ message }: Props) => {
  const {
    data: { content },
  } = message;
  const { fragments, copyableContent } = useMemo(() => parseMessageContent(content), [content]);
  const trackOutputCopied = useGeneratorTrackingCallback('useTrackOutputCopied');
  const trackClick = () => {
    trackOutputCopied({
      content: copyableContent,
      code: content.includes(OPEN_AI_CODE_WRAPPER),
    });
  };

  const renderFooter = () => (
    <div className="copy-magic-conversation-assistant-message__footer">
      <Frame
        direction={Frame.DIRECTIONS.HORIZONTAL}
        gap={Frame.GAPS.LG}
        align={Frame.ALIGNMENTS.CENTER_SPREAD}
      >
        <MessageFeedbackSentiment message={message} />
        <CopyButton value={copyableContent} trackClick={trackClick} />
      </Frame>
    </div>
  );

  return (
    <Card
      className="copy-magic-conversation-assistant-message copy-magic-conversation-assistant-message--assistant copy-magic-conversation-assistant-message--force-full-width"
      compact
      data-testid="message-assistant-fragment"
    >
      {fragments.map((item, index, array) => {
        const lastFragment = array.length - 1 === index;
        return (
          <MessageFragment key={item.content} messageFragment={item}>
            {lastFragment && renderFooter()}
          </MessageFragment>
        );
      })}
    </Card>
  );
};

export default Assistant;
